/**
 * What the intake shows, as plain functions of its state: each photo's tile, the counts, the
 * panel's ambient tone and its state line. `ModelIntake.tsx` only renders these.
 *
 * Spec: the validated canvas of 2026-10-08 (plan `2026_10_08_model_intake_states.md`). One tone
 * names the moment (collecting, working, worth knowing, needs you, ready); each fault sits on its
 * photo in full words; advice is counted and never blocks.
 */
import type {IntakeSlot} from '@core/hooks/useModelIntake'
import type {PhotoFlag, SetAdvisory} from '@core/products/setCheck.server'
import type {DisplayCurrency} from '@core/billing/displayCurrency'
import {CREATE_VERB_PAID, retrainStartButton, startButton} from '@product/narcis/proposition'

export type Tone = 'collecting' | 'working' | 'worth' | 'needs' | 'ready'

/** Why Start set a photo aside: its object is gone, or it belongs to another session. */
export type PhotoMark = 'unavailable' | 'foreign'

export type TileView = 'checking' | 'accepted' | 'not-used' | 'worth' | 'gone'

export interface Tile {
  id: string
  view: TileView
  /** The small word on the photo: `Not used`, `Remove`, or the plain name of a set-level flag. */
  tag?: string
  /** The line under the tag, never cut. Empty where the tag says it all: the sentence for a set-level
   *  flag lives in the rail's ledger, and an accepted photo is confirmed by the meter. */
  reason: string
  flags: PhotoFlag[]
  mark?: PhotoMark
}

const FLAG_TAG: Record<PhotoFlag, string> = {
  near_duplicate: 'Similar',
  outlier: 'Another person?',
}

const MARK_REASON: Record<PhotoMark, string> = {
  unavailable: 'No longer saved. Add it again.',
  foreign: 'From another session.',
}

export function buildTiles(
  slots: IntakeSlot[],
  flagsByKey: Record<string, PhotoFlag[]>,
  marks: Record<string, PhotoMark>,
): Tile[] {
  return slots.map((slot): Tile => {
    if (slot.status === 'verifying') return {id: slot.id, view: 'checking', reason: 'Checking…', flags: []}
    if (slot.status === 'failed') return {id: slot.id, view: 'not-used', tag: 'Not used', reason: slot.reason, flags: []}
    const mark = marks[slot.s3Key]
    if (mark) return {id: slot.id, view: 'gone', tag: 'Remove', reason: MARK_REASON[mark], flags: [], mark}
    const flags = flagsByKey[slot.s3Key] ?? []
    if (flags.length > 0) {
      return {id: slot.id, view: 'worth', tag: flags.map((f) => FLAG_TAG[f]).join(' · '), reason: '', flags}
    }
    return {id: slot.id, view: 'accepted', reason: '', flags: []}
  })
}

export interface Summary {
  /** Photos that count toward the minimum: accepted, with or without advice. */
  accepted: number
  checking: number
  /** Rejected by the checks. */
  notUsed: number
  /** Set aside by a refused Start, by mark. */
  unavailable: number
  foreign: number
  nearDuplicates: number
  otherPerson: number
  /** Distinct things worth knowing: the two photo-level kinds plus each set-level advisory. */
  advice: number
}

export function summarize(tiles: Tile[], advisories: SetAdvisory[]): Summary {
  const count = (pred: (t: Tile) => boolean) => tiles.filter(pred).length
  const nearDuplicates = count((t) => t.flags.includes('near_duplicate'))
  const otherPerson = count((t) => t.flags.includes('outlier'))
  return {
    accepted: count((t) => t.view === 'accepted' || t.view === 'worth'),
    checking: count((t) => t.view === 'checking'),
    notUsed: count((t) => t.view === 'not-used'),
    unavailable: count((t) => t.mark === 'unavailable'),
    foreign: count((t) => t.mark === 'foreign'),
    nearDuplicates,
    otherPerson,
    advice: (nearDuplicates > 0 ? 1 : 0) + (otherPerson > 0 ? 1 : 0) + advisories.length,
  }
}

export interface ToneInput {
  summary: Summary
  min: number
  /** Start, or the payment page, is in flight. */
  starting: boolean
  queued: boolean
  /** The last Start named photos it would not take, or asked for an action. */
  refused: boolean
}

/**
 * The moment's tone. A Start in flight, or a check running, is working; a refusal, or too few photos
 * with some set aside, needs the user; with the minimum met the set is ready, or worth knowing when
 * advice stands. Advice never reaches `needs`: it does not block.
 */
export function deriveTone({summary, min, starting, queued, refused}: ToneInput): Tone {
  if (queued) return 'ready'
  if (starting) return 'working'
  if (refused) return 'needs'
  if (summary.checking > 0) return 'working'
  if (summary.accepted < min && summary.notUsed > 0) return 'needs'
  if (summary.accepted >= min) return summary.advice > 0 ? 'worth' : 'ready'
  return 'collecting'
}

export interface FootInput {
  /** The job is queued, or a model is already being created. */
  inFlight: boolean
  /** Start, or the payment page, is working: the button already says so, and closing the tab does
   *  not cancel the server's Start. */
  starting: boolean
  /** Photos set aside by a refused Start, and whether payment is still to be asked. */
  marked: number
  asksPayment: boolean
  isReady: boolean
  /** Any photo is on the bench, being checked or accepted. */
  hasPhotos: boolean
}

/** The rail's last line, by moment. Ready and waiting for Start says nothing: the price line under the
 *  button is enough. */
export function footLine({inFlight, starting, marked, asksPayment, isReady, hasPhotos}: FootInput): string | null {
  if (inFlight) return 'You can close this page. We will email you when your model is ready.'
  if (starting) return null
  if (marked > 0 && !asksPayment) return 'Nothing was charged twice. Your payment waits for this model.'
  if (!isReady) return hasPhotos ? 'Your photos are saved. You can come back to finish.' : null
  return null
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

export function stateLine(input: ToneInput & {tone: Tone; paidReturn: boolean; confirmingPayment: boolean; marked: number}): string {
  const {tone, summary, min, paidReturn, confirmingPayment, marked, starting, queued, refused} = input
  if (queued) return 'Your model is queued'
  if (confirmingPayment) return 'Confirming your payment…'
  if (starting) return paidReturn ? 'Payment received · starting' : 'Saving your photos'
  if (refused) return marked > 0 ? `Start stopped · ${plural(marked, 'photo')}` : 'Start stopped'
  if (summary.checking > 0) return `Checking ${plural(summary.checking, 'photo')}`
  if (tone === 'needs') return `${plural(summary.notUsed, 'photo')} not used`
  if (tone === 'worth') return `Ready · ${plural(summary.advice, 'thing')} worth knowing`
  if (tone === 'ready') return 'Ready'
  return summary.accepted === 0 ? 'Add your photos' : `Collecting · ${min - summary.accepted} more needed`
}

export interface LedgerRow {
  tone: 'ok' | 'needs' | 'working' | 'worth'
  text: string
  sub?: string
  count?: number
}

/** The rail's ledger: what was counted, what was set aside, what stands as advice. Rows with
 *  nothing to say are absent. */
export function ledgerRows(summary: Summary, advisories: SetAdvisory[]): LedgerRow[] {
  const rows: LedgerRow[] = [{tone: 'ok', text: 'Accepted', count: summary.accepted}]
  if (summary.notUsed > 0) {
    rows.push({tone: 'needs', text: 'Not used', sub: 'Marked in red. Remove or replace them.', count: summary.notUsed})
  }
  if (summary.unavailable > 0) {
    rows.push({tone: 'needs', text: 'No longer saved', sub: 'Marked with a red dashed line.', count: summary.unavailable})
  }
  if (summary.foreign > 0) {
    rows.push({tone: 'needs', text: 'From another session', sub: 'Marked with a red dashed line.', count: summary.foreign})
  }
  if (summary.checking > 0) rows.push({tone: 'working', text: 'Checking', count: summary.checking})
  if (summary.nearDuplicates > 0) {
    rows.push({
      tone: 'worth',
      text: 'Near-duplicates',
      sub: 'Different photos make a better model.',
      count: summary.nearDuplicates,
    })
  }
  if (summary.otherPerson > 0) {
    rows.push({tone: 'worth', text: 'Another person?', sub: 'Check these are you. They still count.', count: summary.otherPerson})
  }
  if (advisories.includes('pose_spread')) {
    rows.push({tone: 'worth', text: 'Angles', sub: 'All face the camera. One from the side helps.'})
  }
  return rows
}

// ── The Start control ────────────────────────────────────────────────────────

export interface StartControlInput {
  isReady: boolean
  accepted: number
  min: number
  /** Photos set aside by a refused Start: the same button carries on without them. */
  continuing: boolean
  /** Payment is still to be asked. */
  asksPayment: boolean
  isRetrain: boolean
  isAuthenticated: boolean
  /** The consent box stands ticked (a retrain's stands ticked from the start). */
  consentGiven: boolean
  /** The server answered that the session is lost: sign-in is owed whatever the page believed. */
  sessionLost: boolean
  currency: DisplayCurrency
}

/** What the rail offers under the consent: a button saying what its next press does, or the sign-in. */
export type StartControl = {kind: 'signin'} | {kind: 'button'; label: string}

/**
 * The word "pay" sits on the one press that opens Stripe: ready, ticked, signed in, payment asked.
 * Before the tick the button only creates; ticked and signed out, it gives way to the sign-in.
 */
export function startControl(input: StartControlInput): StartControl {
  const {isReady, accepted, min, continuing, asksPayment, isRetrain, isAuthenticated, consentGiven, sessionLost, currency} = input
  if (!isReady) {
    const missing = min - accepted
    return {kind: 'button', label: `${missing} more photo${missing === 1 ? '' : 's'} to start`}
  }
  if (sessionLost || (!isAuthenticated && consentGiven)) return {kind: 'signin'}
  if (continuing) return {kind: 'button', label: `Continue with ${accepted} photo${accepted === 1 ? '' : 's'}`}
  if (consentGiven && asksPayment) {
    return {kind: 'button', label: isRetrain ? retrainStartButton(currency) : startButton(currency)}
  }
  return {kind: 'button', label: isRetrain ? 'Update my model →' : CREATE_VERB_PAID}
}

// ── Start refusals ───────────────────────────────────────────────────────────

export type StartActionResponse =
  | {status: 'queued'; jobId: string; payment: {amount: number; currency: string; transactionId: string}}
  | {status: 'error'; error: string; keys?: string[]}
  | {status: 'payment-required'}

/** The marks a refused Start leaves: `photos-unavailable` names photos whose object is gone,
 *  `invalid-photo-count` the ones this session does not own. Any other answer marks nothing. */
export function refusalMarks(data: StartActionResponse | undefined): Record<string, PhotoMark> {
  if (data?.status !== 'error' || !Array.isArray(data.keys)) return {}
  const mark: PhotoMark | null =
    data.error === 'photos-unavailable' ? 'unavailable' : data.error === 'invalid-photo-count' ? 'foreign' : null
  if (!mark) return {}
  return Object.fromEntries(data.keys.filter((k): k is string => typeof k === 'string').map((k) => [k, mark]))
}

/** How many quiet tries a paid return makes when its payment cannot be confirmed yet. */
export const PAYMENT_RETRIES = 3
export const PAYMENT_RETRY_MS = 4000
